<template>
  <b-modal
    id="task-form-modal"
    :visible="value"
    :title="modalTitle"
    size="lg"
    scrollable
    ok-title="Save"
    :ok-disabled="submitting || isReadOnlyMode"
    :hide-footer="isReadOnlyMode"
    cancel-variant="outline-secondary"
    no-close-on-backdrop
    @change="$emit('input', $event)"
    @ok="handleOk"
    @hidden="onHidden"
  >
    <b-alert v-if="isReadOnlyMode" show variant="warning" class="p-2">
      <feather-icon icon="LockIcon" size="14" class="mr-50" />
      Only the creator can update this task. You have read-only access.
    </b-alert>

    <b-alert v-else-if="isStatusOnlyMode" show variant="info" class="p-2">
      <feather-icon icon="InfoIcon" size="14" class="mr-50" />
      This task was assigned by <strong>{{ creatorName || 'your manager' }}</strong> — you can only update its status.
    </b-alert>

    <b-alert v-if="isResponsibilityMode" show variant="info" class="p-2">
      <feather-icon icon="InfoIcon" size="14" class="mr-50" />
      Project responsibilities are managed from the Projects team panel.
      <template v-if="canEditProjectStatus"> You can update this project's status below.</template>
      <template v-else-if="statusEditable"> You can update completion status below.</template>
    </b-alert>

    <b-alert v-if="!isEditMode && presetAssigneeName" show variant="info" class="p-2">
      <feather-icon icon="InfoIcon" size="14" class="mr-50" />
      Pre-assigned to <strong>{{ presetAssigneeName }}</strong> — change below if needed.
    </b-alert>

    <b-alert v-if="isEditMode && task && task.recurringTaskId" show variant="secondary" class="p-2">
      <feather-icon icon="RepeatIcon" size="14" class="mr-50" />
      This task was auto-generated from a recurring series — editing it only affects this one occurrence, not future ones.
    </b-alert>

    <validation-observer ref="taskFormRules">
      <b-form @submit.prevent>
        <!-- Task details -->
        <h6 class="task-form-section-title small font-weight-bold mb-75">
          <feather-icon icon="EditIcon" size="14" class="mr-50" />
          Task details
        </h6>
        <b-row class="task-form-section">
          <b-col cols="12">
            <b-form-group label-for="task-title">
              <template #label>
                Title *
                <feather-icon
                  v-if="!detailsEditable"
                  :aria-label="lockedFieldsMessage"
                  icon="LockIcon"
                  size="12"
                  class="text-muted ml-25"
                />
              </template>
              <validation-provider #default="{ errors }" name="Title" :rules="detailsEditable ? 'required' : ''">
                <b-form-input
                  id="task-title"
                  v-model="form.title"
                  :disabled="!detailsEditable"
                  placeholder="e.g. Prepare Q3 client report"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>

          <b-col cols="12">
            <b-form-group label-for="task-description">
              <template #label>
                Description *
                <feather-icon
                  v-if="!detailsEditable"
                  :aria-label="lockedFieldsMessage"
                  icon="LockIcon"
                  size="12"
                  class="text-muted ml-25"
                />
              </template>
              <validation-provider #default="{ errors }" name="Description" :rules="detailsEditable ? 'required' : ''">
                <b-form-textarea
                  id="task-description"
                  v-model="form.description"
                  :disabled="!detailsEditable"
                  :state="errors.length > 0 ? false : null"
                  rows="3"
                  placeholder="What needs to be done?"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
        </b-row>

        <!-- Schedule & project -->
        <h6 class="task-form-section-title small font-weight-bold mb-75 mt-1">
          <feather-icon icon="CalendarIcon" size="14" class="mr-50" />
          Schedule &amp; project
        </h6>
        <b-row class="task-form-section">
          <b-col cols="6" md="3">
            <b-form-group label-for="task-priority">
              <template #label>
                Priority *
                <feather-icon
                  v-if="!priorityEditable"
                  :aria-label="lockedFieldsMessage"
                  icon="LockIcon"
                  size="12"
                  class="text-muted ml-25"
                />
              </template>
              <validation-provider #default="{ errors }" name="Priority" :rules="priorityEditable ? 'required' : ''">
                <b-form-select
                  id="task-priority"
                  v-model="form.priority"
                  :options="priorityOptions"
                  value-field="key"
                  text-field="label"
                  :disabled="!priorityEditable"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>

          <b-col cols="6" md="3">
            <b-form-group label-for="task-status">
              <template #label>
                Status *
                <feather-icon
                  v-if="!statusEditable"
                  aria-label="Only the task owner or the manager who assigned it can change its status."
                  icon="LockIcon"
                  size="12"
                  class="text-muted ml-25"
                />
              </template>
              <validation-provider #default="{ errors }" name="Status" :rules="statusEditable ? 'required' : ''">
                <b-form-select
                  id="task-status"
                  v-model="form.status"
                  :options="effectiveStatusOptions"
                  value-field="key"
                  text-field="label"
                  :disabled="!statusEditable"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>

          <b-col cols="6" md="3">
            <b-form-group label-for="task-due-date">
              <template #label>
                Due date *
                <feather-icon
                  v-if="!detailsEditable"
                  :aria-label="lockedFieldsMessage"
                  icon="LockIcon"
                  size="12"
                  class="text-muted ml-25"
                />
              </template>
              <validation-provider #default="{ errors }" name="Due Date" :rules="detailsEditable ? 'required' : ''">
                <flat-pickr
                  id="task-due-date"
                  v-model="form.dueDate"
                  class="form-control"
                  :class="{ 'is-invalid': errors.length > 0 }"
                  :disabled="!detailsEditable"
                  placeholder="YYYY-MM-DD"
                  :config="{ enableTime: false, dateFormat: 'Y-m-d' }"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>

          <!--
            Deadline TIME — deliberately a SEPARATE field from Due Date
            above rather than switching that field to a single datetime
            picker: `dueDate` alone already drives escalation/archive/
            recurrence date math throughout `task-config.js` (day-level
            comparisons only), so keeping it a bare `Y-m-d` value avoids
            touching any of that logic. `dueTime` is purely an optional
            "what time today" display refinement — a `flat-pickr` time-only
            picker (`noCalendar: true`), stored as `H:i` 24h text so it
            sorts/compares trivially if ever needed; display formatting
            (12h with AM/PM) happens at render time — see
            `formatDueDateTime()` in `task-config.js`.
          -->
          <b-col cols="6" md="3">
            <b-form-group label-for="task-due-time">
              <template #label>
                Due time
                <feather-icon
                  v-if="!detailsEditable"
                  :aria-label="lockedFieldsMessage"
                  icon="LockIcon"
                  size="12"
                  class="text-muted ml-25"
                />
              </template>
              <flat-pickr
                id="task-due-time"
                v-model="form.dueTime"
                class="form-control"
                :disabled="!detailsEditable"
                placeholder="Optional"
                :config="{ enableTime: true, noCalendar: true, dateFormat: 'H:i', altInput: true, altFormat: 'h:i K' }"
              />
            </b-form-group>
          </b-col>

          <b-col cols="12" md="6">
            <b-form-group label-for="task-project">
              <template #label>
                Project <span class="text-muted font-weight-normal">(optional)</span>
                <feather-icon
                  v-if="!detailsEditable"
                  :aria-label="lockedFieldsMessage"
                  icon="LockIcon"
                  size="12"
                  class="text-muted ml-25"
                />
              </template>
              <b-form-select
                id="task-project"
                v-model="form.projectId"
                :options="projectSelectOptions"
                value-field="value"
                text-field="text"
                :disabled="!detailsEditable"
              >
                <template #first>
                  <option :value="null">No project</option>
                </template>
              </b-form-select>
            </b-form-group>
          </b-col>

          <b-col v-if="showProjectStatusField" cols="12" md="6">
            <b-form-group label-for="task-project-status">
              <template #label>
                Project status
                <feather-icon
                  v-if="!canEditProjectStatus"
                  aria-label="Only the project manager can change project status."
                  icon="LockIcon"
                  size="12"
                  class="text-muted ml-25"
                />
              </template>
              <b-form-select
                v-if="canEditProjectStatus"
                id="task-project-status"
                v-model="projectStatus"
                :options="projectStatusOptions"
                value-field="key"
                text-field="label"
              />
              <div v-else class="pt-50">
                <b-badge :variant="`light-${projectStatusBadgeVariant}`" class="project-status-badge">
                  {{ projectStatus }}
                </b-badge>
              </div>
            </b-form-group>
          </b-col>
        </b-row>

        <!-- Assignment -->
        <h6 class="task-form-section-title small font-weight-bold mb-75 mt-1">
          <feather-icon icon="UserIcon" size="14" class="mr-50" />
          Assignment
        </h6>
        <b-row class="task-form-section">
          <b-col cols="12" :md="assignToMultiple ? 8 : 6">
            <b-form-group label-for="task-assignee">
              <template #label>
                Assign to *
                <feather-icon
                  v-if="!detailsEditable"
                  :aria-label="lockedFieldsMessage"
                  icon="LockIcon"
                  size="12"
                  class="text-muted ml-25"
                />
              </template>
              <p v-if="assigneeScopeHint" class="text-muted small mb-50">{{ assigneeScopeHint }}</p>
              <!--
                Multi-select ("Assign to multiple?") is create-only — see
                `canAssignToMultiple`'s doc — so once `assignToMultiple` is
                on, `form.assignedTo` (the normal single-assignee field,
                still used verbatim by every OTHER path — edit mode,
                priority/status-only overrides, single-assignee create) is
                simply not rendered/validated at all here; `form.
                assignedToList` is local-only form state with its own
                manual "at least 1" check in `handleOk()` (mirroring
                `weeklyDaysError`'s pattern just below, for the same reason
                — vee-validate's `required` doesn't natively cover
                "non-empty array/multi-select").

                Rendered as a `<b-dropdown>` containing a plain checkbox
                LIST rather than `<b-form-select multiple>` — a native HTML
                multi-select's UX (ctrl/cmd-click to select more than one,
                no visible checkmarks) is genuinely worse than a checklist
                for this. Checkboxes here are plain content in the
                dropdown's default slot, not `<b-dropdown-item>`s, so
                clicking one does NOT trigger BootstrapVue's close-on-click
                behavior (that only fires for `.dropdown-item`s and actual
                outside clicks) — the menu stays open across multiple
                picks, same as `v-select`'s old `:close-on-select="false"`
                behavior. `boundary="window"` + `positionFixed` is the same
                defensive pairing already used for other dropdowns
                elsewhere in this module that live inside an
                overflow-bearing ancestor (here, `<b-modal scrollable>`'s
                own scrolling body) — see `TaskCard.vue`'s Priority
                dropdown for the identical pattern.
              -->
              <b-dropdown
                v-if="assignToMultiple"
                id="task-assignee"
                variant="link"
                toggle-class="form-control assignee-multiselect-toggle d-flex align-items-center justify-content-between"
                block
                no-caret
                boundary="window"
                :popper-opts="{ positionFixed: true }"
                :disabled="!detailsEditable"
              >
                <template #button-content>
                  <span class="text-truncate">{{ assignedToListSummary }}</span>
                  <feather-icon icon="ChevronDownIcon" size="14" class="ml-50 flex-shrink-0" />
                </template>
                <div class="assignee-multiselect-menu" @click.stop>
                  <b-form-checkbox
                    v-for="option in assigneeOptions"
                    :key="option.value"
                    v-model="form.assignedToList"
                    :value="option.value"
                    class="assignee-multiselect-item"
                  >
                    {{ option.label }}
                  </b-form-checkbox>
                </div>
              </b-dropdown>
              <validation-provider v-else #default="{ errors }" name="Assign To" :rules="assignToFieldRequired ? 'required' : ''">
                <b-form-select
                  id="task-assignee"
                  v-model="form.assignedTo"
                  :options="assigneeOptions"
                  value-field="value"
                  text-field="label"
                  :disabled="!detailsEditable"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
              <small v-if="assignToMultiple && multiAssigneeError" class="text-danger">{{ multiAssigneeError }}</small>
              <!--
                Manager-only, create-only toggle — see `canAssignToMultiple`.
                Placed directly under the field it controls (same
                "checkbox right next to what it toggles" convention as
                "No end date" next to the recurrence end-date field below).
              -->
              <b-form-checkbox
                v-if="canAssignToMultiple"
                v-model="assignToMultiple"
                switch
                class="mt-50"
              >
                Assign to multiple people
              </b-form-checkbox>
            </b-form-group>
          </b-col>
        </b-row>

        <!-- Attachment & recurrence -->
        <h6 class="task-form-section-title small font-weight-bold mb-75 mt-1">
          <feather-icon icon="PaperclipIcon" size="14" class="mr-50" />
          More options
        </h6>
        <b-row class="task-form-section">
          <!--
            Attachment — Vuexy Kanban-style supporting file on create/edit.
            Editable whenever details are editable (creator / manager who
            owns the task — same gate as title/description). Assignees in
            status-only mode still see a read-only download link when a
            file exists. Multipart field: `attachment` (see task-config).
          -->
          <b-col cols="12">
            <b-card no-body class="p-1 border task-attachment-card mb-1">
              <div class="d-flex align-items-center mb-50">
                <span class="small font-weight-bold">
                  Attachment
                </span>
                <feather-icon
                  v-if="!detailsEditable"
                  :aria-label="lockedFieldsMessage"
                  icon="LockIcon"
                  size="12"
                  class="text-muted ml-25"
                />
                <span class="text-muted small ml-auto">PDF, Word, Excel, or image</span>
              </div>

              <div
                v-if="displayedAttachment"
                class="d-flex align-items-center flex-wrap task-attachment-row"
              >
                <feather-icon icon="FileTextIcon" size="14" class="text-primary flex-shrink-0" />
                <a
                  v-if="displayedAttachment.url"
                  :href="displayedAttachment.url"
                  target="_blank"
                  rel="noopener"
                  class="text-truncate flex-grow-1 min-width-0 ml-50"
                  @click.stop
                >
                  {{ displayedAttachment.name }}
                </a>
                <span v-else class="text-truncate text-body flex-grow-1 min-width-0 ml-50">
                  {{ displayedAttachment.name }}
                </span>
                <b-button
                  v-if="detailsEditable"
                  size="sm"
                  variant="outline-danger"
                  class="px-50 py-25 ml-50 flex-shrink-0"
                  aria-label="Remove attachment"
                  @click.prevent="clearAttachment"
                >
                  <feather-icon icon="XIcon" size="12" />
                </b-button>
              </div>

              <b-form-file
                v-if="detailsEditable && !displayedAttachment"
                id="task-attachment"
                v-model="attachmentFile"
                :accept="attachmentAccept"
                placeholder="Choose a file or drop it here…"
                drop-placeholder="Drop file here…"
                browse-text="Browse"
                class="task-attachment-file"
              />
              <div v-else-if="detailsEditable && displayedAttachment && !attachmentFile && !replacingAttachment" class="d-flex flex-wrap mt-25" style="gap: 8px;">
                <b-button
                  size="sm"
                  variant="outline-primary"
                  @click.prevent="replaceAttachment"
                >
                  <feather-icon icon="PaperclipIcon" size="12" class="mr-25" />Replace
                </b-button>
              </div>
              <b-form-file
                v-if="detailsEditable && replacingAttachment && !attachmentFile"
                id="task-attachment-replace"
                v-model="attachmentFile"
                :accept="attachmentAccept"
                class="mt-50 task-attachment-file"
                placeholder="Choose a replacement file…"
                drop-placeholder="Drop file here…"
                browse-text="Browse"
              />
            </b-card>
          </b-col>

          <!--
            Recurring section — same toggle-reveals-config UX pattern as the
            Invoice wizard's "Has Package Code?" switch + `b-collapse`
            (`WizardClientDetailsStep.vue`). Deliberately NOT a full RRULE/
            iCal builder — just frequency + interval + (weekly-only)
            specific days + an optional end date, per spec. Locked behind
            the same `detailsEditable` gate as every other detail field
            above (recurrence setup is just another task detail — see
            `canConfigureRecurrence()` in `task-permissions.js`).
          -->
          <b-col cols="12">
            <div class="d-flex align-items-center">
              <b-form-checkbox
                v-model="form.isRecurring"
                switch
                :disabled="!detailsEditable"
                class="mb-0"
              >
                Recurring task
              </b-form-checkbox>
              <feather-icon
                v-if="!detailsEditable"
                :aria-label="lockedFieldsMessage"
                icon="LockIcon"
                size="12"
                class="text-muted ml-25"
              />
            </div>

            <b-collapse v-model="form.isRecurring">
              <b-row class="mt-1">
                <b-col cols="12" md="4">
                  <b-form-group label-for="task-recurrence-frequency">
                    <template #label>Repeats *</template>
                    <!--
                      `<b-form-select>`, NOT `<v-select>` — this whole
                      modal has since been converted off `<v-select>`
                      entirely (Priority/Status/Assign-To included), but
                      THIS field is where the bug was originally caught:
                      it sits inside the "Recurring Task?" `<b-collapse>`
                      above, which — same as `<b-modal scrollable>`'s own
                      scrolling body — is an `overflow`-bearing ancestor.
                      `<v-select>`'s
                      dropdown-menu is a plain in-DOM, absolutely-positioned
                      sibling (not portaled to `document.body` unless
                      `append-to-body` is set), so it gets its box
                      measured/clipped against THAT ancestor chain rather
                      than the viewport — this is exactly what rendered as
                      a malformed, content-less colored bar when the
                      dropdown opened. A native `<select>` (what
                      `<b-form-select>` renders under the hood) has its
                      OPEN option list drawn entirely by the browser itself,
                      completely outside normal CSS layout/overflow/z-index
                      — there's no floating DOM element of ours for any
                      ancestor to clip or mis-measure, so this whole class
                      of bug cannot recur here regardless of what this
                      field ever gets nested inside in the future.
                    -->
                    <validation-provider #default="{ errors }" name="Repeats" :rules="form.isRecurring ? 'required' : ''">
                      <b-form-select
                        id="task-recurrence-frequency"
                        v-model="form.recurrenceFrequency"
                        :options="recurrenceFrequencyOptions"
                        value-field="key"
                        text-field="label"
                        :disabled="!detailsEditable"
                        :state="errors.length > 0 ? false : null"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>

                <b-col cols="12" md="4">
                  <b-form-group label-for="task-recurrence-interval">
                    <template #label>Every</template>
                    <b-input-group :append="intervalUnitLabel">
                      <b-form-input
                        id="task-recurrence-interval"
                        v-model.number="form.recurrenceInterval"
                        type="number"
                        min="1"
                        :disabled="!detailsEditable || recurrenceIntervalDisabled"
                      />
                    </b-input-group>
                    <small v-if="recurrenceIntervalDisabled" class="text-muted">Ignored when specific weekdays are picked below.</small>
                  </b-form-group>
                </b-col>

                <b-col cols="12" md="4">
                  <b-form-group label-for="task-recurrence-end-date">
                    <template #label>Ends On</template>
                    <flat-pickr
                      v-if="!noEndDate"
                      id="task-recurrence-end-date"
                      v-model="form.recurrenceEndDate"
                      class="form-control"
                      :disabled="!detailsEditable"
                      placeholder="YYYY-MM-DD"
                      :config="{ enableTime: false, dateFormat: 'Y-m-d' }"
                    />
                    <b-form-checkbox v-model="noEndDate" :disabled="!detailsEditable" :class="{ 'mt-50': !noEndDate }">
                      No end date
                    </b-form-checkbox>
                  </b-form-group>
                </b-col>

                <!--
                  Monthly's explicit-choice counterpart to "On these days *"
                  below — same "don't silently derive it from whatever day
                  the Due Date happens to be" rationale, mirroring the
                  weekly pattern exactly (see `computeNextRecurrenceDate()`'s
                  doc in `task-config.js`). Auto-defaulted from the current
                  Due Date's day-of-month the first time the user switches
                  TO Monthly (`applyDefaultDayOfMonth()`, called from the
                  `recurrenceFrequency` watcher below) — a nice UX touch
                  so this never starts blank — but freely overridable
                  afterward, and never re-defaulted once set.
                -->
                <b-col v-if="form.recurrenceFrequency === 'monthly'" cols="12" md="4">
                  <b-form-group label-for="task-recurrence-day-of-month">
                    <template #label>On this day of the month *</template>
                    <validation-provider #default="{ errors }" name="Day of Month" :rules="form.recurrenceFrequency === 'monthly' ? 'required' : ''">
                      <b-form-select
                        id="task-recurrence-day-of-month"
                        v-model="form.recurrenceDayOfMonth"
                        :options="dayOfMonthOptions"
                        :disabled="!detailsEditable"
                        :state="errors.length > 0 ? false : null"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                    <small class="text-muted">If a month has fewer days, this task will fall on the last day of that month instead.</small>
                  </b-form-group>
                </b-col>

                <b-col v-if="form.recurrenceFrequency === 'weekly'" cols="12">
                  <b-form-group>
                    <template #label>On these days *</template>
                    <div class="d-flex flex-wrap" style="gap: 4px 14px;">
                      <b-form-checkbox
                        v-for="day in daysOfWeekOptions"
                        :key="day.value"
                        v-model="form.recurrenceDaysOfWeek"
                        :value="day.value"
                        :disabled="!detailsEditable"
                      >
                        {{ day.label }}
                      </b-form-checkbox>
                    </div>
                    <small v-if="weeklyDaysError" class="text-danger">{{ weeklyDaysError }}</small>
                  </b-form-group>
                </b-col>
              </b-row>
            </b-collapse>
          </b-col>
        </b-row>
      </b-form>
    </validation-observer>

    <!--
      Edit history — read-only audit trail, shown for anyone who can open
      this modal at all (per spec: viewing a task already means it passed
      `visibleTasks()` upstream in `TaskBoard.vue`, so there's no separate
      permission gate here). Only meaningful in edit mode — a brand-new
      task has no history yet. Deliberately a plain collapsible list, not
      a tab or a fancy diff UI, per spec ("keep this genuinely simple and
      readable").
    -->
    <template v-if="isEditMode">
      <hr class="my-1">
      <b-button
        variant="link"
        size="sm"
        class="p-0 d-flex align-items-center task-history-toggle"
        @click="historyPanelOpen = !historyPanelOpen"
      >
        <feather-icon :icon="historyPanelOpen ? 'ChevronDownIcon' : 'ChevronRightIcon'" size="14" class="mr-25" />
        History<span v-if="sortedHistoryEntries.length">&nbsp;({{ sortedHistoryEntries.length }})</span>
      </b-button>
      <b-collapse v-model="historyPanelOpen" class="mt-1">
        <div v-if="historyLoading" class="text-center text-muted small py-2">
          <b-spinner small class="mr-50" />Loading history…
        </div>
        <div v-else-if="!sortedHistoryEntries.length" class="text-muted small py-1">
          No changes recorded yet.
        </div>
        <ul v-else class="task-history-list list-unstyled mb-0 pr-1">
          <li v-for="(entry, index) in sortedHistoryEntries" :key="index" class="task-history-item small">
            <feather-icon icon="EditIcon" size="12" class="text-muted mr-50" />
            <strong>{{ entry.fieldLabel || entry.field }}</strong> changed from
            <em>{{ entry.oldDisplay || formatHistoryValue(entry.oldValue) }}</em> to
            <em>{{ entry.newDisplay || formatHistoryValue(entry.newValue) }}</em>
            by <strong>{{ entry.changedByName || entry.changedBy || 'Unknown' }}</strong>
            on {{ formatHistoryDate(entry.changedAt) }}
          </li>
        </ul>
      </b-collapse>
    </template>
  </b-modal>
</template>

<script>
import {
  BModal, BForm, BFormGroup, BFormInput, BFormSelect, BFormTextarea, BFormCheckbox, BFormFile, BInputGroup, BCollapse, BRow, BCol, BAlert, BButton, BDropdown, BSpinner, BCard, BBadge,
} from 'bootstrap-vue'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import flatPickr from 'vue-flatpickr-component'
import { required } from '@validations'
import taskService from '@/services/task-service'
import {
  TASK_STATUS_COLUMNS,
  TASK_PRIORITY_LEVELS,
  DEFAULT_TASK_STATUS,
  DEFAULT_TASK_PRIORITY,
  RECURRENCE_FREQUENCIES,
  DEFAULT_RECURRENCE_FREQUENCY,
  RECURRENCE_DAYS_OF_WEEK,
  RECURRENCE_DAY_OF_MONTH_OPTIONS,
  TASK_ATTACHMENT_ACCEPT,
  taskId,
  taskOwnerName,
  normalizeTaskStatus,
  normalizeTaskAttachment,
  normalizeChangeHistory,
} from '@/utils/task-config'
import {
  canEditTaskDetails,
  canChangePriority,
  canChangeStatus,
  getLockedFieldsMessage,
  getEmpId,
} from '@/utils/task-permissions'
import {
  filterAllowedStatusOptions,
  getProjectStatusDenialReason,
  hasAllowedStatusTransition,
  canManageProject,
} from '@/utils/project-permissions'
import {
  projectId as getProjectId,
  normalizeMembers,
  reconcileProjectMembers,
  PROJECT_STATUSES,
  PROJECT_STATUS_OPTIONS,
  normalizeProjectStatus,
  projectStatusVariant,
} from '@/utils/project-config'
import { isProjectResponsibilityTask } from '@/utils/project-responsibility-tasks'

/** Treat null, undefined, and blank strings as "no project". */
function normalizeProjectId(value) {
  if (value === undefined || value === null) return null
  const trimmed = String(value).trim()
  return trimmed === '' ? null : trimmed
}

function blankForm(currentUser, presetAssignee) {
  return {
    id: null,
    title: '',
    description: '',
    status: DEFAULT_TASK_STATUS,
    priority: DEFAULT_TASK_PRIORITY,
    assignedTo: presetAssignee != null ? String(presetAssignee) : getEmpId(currentUser),
    // "Assign to multiple?" (create-only, see `canAssignToMultiple`) —
    // local-only form state, never read/sent unless `assignToMultiple` is
    // on; the normal single-assignee `assignedTo` field above is left
    // completely untouched by this toggle either way.
    assignedToList: [],
    dueDate: null,
    dueTime: null,
    isRecurring: false,
    recurrenceFrequency: DEFAULT_RECURRENCE_FREQUENCY,
    recurrenceInterval: 1,
    recurrenceDaysOfWeek: [],
    // Monthly's explicit-choice counterpart to `recurrenceDaysOfWeek` —
    // starts `null` (blank select) on a brand-new task; auto-defaulted
    // from the Due Date the moment the user switches to Monthly — see
    // `applyDefaultDayOfMonth()`.
    recurrenceDayOfMonth: null,
    recurrenceEndDate: null,
    // Generation-bookkeeping fields, invisible in the form UI — carried
    // through unchanged so `handleOk`'s emitted payload never accidentally
    // drops what `TaskBoard.vue` needs to preserve on edit (see its own
    // doc comments for why these two specifically are handled outside the
    // normal form-field flow).
    recurrenceLastGeneratedDate: null,
    recurringTaskId: null,
    projectId: null,
    projectName: null,
  }
}

export default {
  name: 'TaskFormModal',
  components: {
    BModal, BForm, BFormGroup, BFormInput, BFormSelect, BFormTextarea, BFormCheckbox, BFormFile, BInputGroup, BCollapse, BRow, BCol, BAlert, BButton, BDropdown, BSpinner, BCard, BBadge,
    ValidationObserver, ValidationProvider, flatPickr,
  },
  model: { prop: 'value', event: 'input' },
  props: {
    value: { type: Boolean, default: false },
    task: { type: Object, default: null },
    currentUser: { type: Object, default: () => ({}) },
    teamMembers: { type: Array, default: () => [] },
    submitting: { type: Boolean, default: false },
    /** Pre-selected assignee (emp_id) for the "quick add" per-column button in the team Kanban board — create mode only. */
    presetAssignee: { type: String, default: null },
    /** Projects the current user can assign tasks to. */
    projects: { type: Array, default: () => [] },
    /** All visible projects — used to resolve project role for an existing task. */
    allProjects: { type: Array, default: () => [] },
    /** Pre-selected project when creating from a filtered project view. */
    presetProjectId: { type: [String, Number], default: null },
  },
  data() {
    return {
      required,
      statusOptions: TASK_STATUS_COLUMNS,
      priorityOptions: TASK_PRIORITY_LEVELS,
      projectStatusOptions: PROJECT_STATUS_OPTIONS,
      recurrenceFrequencyOptions: RECURRENCE_FREQUENCIES,
      daysOfWeekOptions: RECURRENCE_DAYS_OF_WEEK,
      dayOfMonthOptions: RECURRENCE_DAY_OF_MONTH_OPTIONS,
      form: blankForm(this.currentUser, this.presetAssignee),
      /** Project status (Active / On Hold / …) — edited separately from task status when the user can manage the project. */
      projectStatus: PROJECT_STATUSES.ACTIVE,
      /** Set only on a failed submit attempt so the "pick at least one day" message doesn't show before the user's even tried — clears itself the instant a day gets picked (see `weeklyDaysError` below). */
      weeklyDaysSubmitAttempted: false,
      /** "Assign to multiple?" toggle — create-only UI state, always reset to `false` whenever the modal opens (see `resetForm()`); see `canAssignToMultiple` for who even sees it. */
      assignToMultiple: false,
      /** Mirrors `weeklyDaysSubmitAttempted`'s pattern — only set on a failed submit attempt, for `multiAssigneeError` below. */
      multiAssigneeSubmitAttempted: false,
      /** Collapsed by default every time the modal opens — see `resetForm()`. */
      historyPanelOpen: false,
      historyLoading: false,
      /** Raw entries for the currently-open task — either the dedicated endpoint's response, or `task.changeHistory` when that endpoint isn't implemented yet. See `loadHistory()`. */
      historyEntries: [],
      /** Newly picked File (not yet saved) — never put on `form` itself so `buildTaskPayload` stays JSON-safe. */
      attachmentFile: null,
      /** Existing server/local attachment snapshot when the modal opened (or null). */
      existingAttachment: null,
      /** User clicked Remove on the existing file before save. */
      removeAttachment: false,
      /** Show the replace file picker while an existing/new name is still listed. */
      replacingAttachment: false,
      attachmentAccept: TASK_ATTACHMENT_ACCEPT,
    }
  },
  computed: {
    /**
     * What the Attachment row should show right now: a newly picked file
     * name, or the still-kept existing attachment (unless removed).
     */
    displayedAttachment() {
      if (this.attachmentFile) {
        return { name: this.attachmentFile.name || 'Attachment', url: null }
      }
      if (this.removeAttachment) return null
      return this.existingAttachment
    },
    isEditMode() {
      return !!this.task
    },
    isResponsibilityMode() {
      return this.isEditMode && isProjectResponsibilityTask(this.task)
    },
    modalTitle() {
      if (this.isResponsibilityMode) return 'Project responsibility'
      return this.isEditMode ? 'Edit Task' : 'New Task'
    },
    detailsEditable() {
      if (!this.isEditMode) return true
      return canEditTaskDetails(this.currentUser, this.task)
    },
    priorityEditable() {
      if (!this.isEditMode) return true
      return canChangePriority(this.currentUser, this.task, this.teamMembers)
    },
    statusEditable() {
      if (!this.isEditMode) return true
      if (!canChangeStatus(this.currentUser, this.task)) return false
      // Responsibility completion mirrors Kanban drag — not project-role task gates.
      if (this.isResponsibilityMode) return true
      if (!this.resolvedProject) return true
      return hasAllowedStatusTransition(
        this.currentUser,
        this.resolvedProject,
        this.task,
        this.statusOptions,
      )
    },
    isPriorityOnlyMode() {
      return false
    },
    canEditProjectStatus() {
      return !!this.resolvedProject && canManageProject(this.currentUser, this.resolvedProject)
    },
    showProjectStatusField() {
      return this.isEditMode && !!this.resolvedProject
    },
    projectStatusBadgeVariant() {
      return projectStatusVariant(normalizeProjectStatus({ status: this.projectStatus }))
    },
    isReadOnlyMode() {
      if (!this.isEditMode || !this.task) return false
      return !this.detailsEditable
        && !this.statusEditable
        && !this.priorityEditable
        && !this.canEditProjectStatus
    },
    /** Assignee/co-assignee path — may change status but not task details. */
    isStatusOnlyMode() {
      if (!this.isEditMode || !this.task) return false
      if (this.isResponsibilityMode) return false
      return this.statusEditable && !this.detailsEditable && !this.priorityEditable
    },
    /** Name of the manager who created/assigned this task — used in the locked-fields banner. `null` when unresolvable. */
    creatorName() {
      if (!this.task) return null
      const name = taskOwnerName(this.task)
      return name && name !== '-' ? name : null
    },
    /** Accessible label for the lock icon next to any field the current user can't edit. `null` when nothing's locked. */
    lockedFieldsMessage() {
      return getLockedFieldsMessage(this.currentUser, this.task)
    },
    /** Display name for `presetAssignee`, resolved from the same team-member list the "Assign To" dropdown uses. */
    presetAssigneeName() {
      if (this.presetAssignee == null) return null
      if (String(this.presetAssignee) === this.currentEmpId) return null
      const match = this.assigneeOptions.find(o => String(o.value) === String(this.presetAssignee))
      if (match) return match.label
      const member = this.teamMembers.find(m => String(m.emp_id ?? m.id) === String(this.presetAssignee))
      if (!member) return null
      return member.first_name ? `${member.first_name} ${member.last_name || ''}`.trim() : (member.name || member.title || String(this.presetAssignee))
    },
    currentEmpId() {
      return getEmpId(this.currentUser)
    },
    assignToFieldRequired() {
      return this.detailsEditable && !this.assignToMultiple
    },
    projectSelectOptions() {
      return (this.projects || []).map(p => ({
        value: String(p.id),
        text: p.name || `Project ${p.id}`,
      }))
    },
    /**
     * "Assign to multiple?" is create-only (each clone becomes its own,
     * independently editable task the moment it exists — there's nothing
     * left to "multi-assign" once a task already exists) AND manager-only
     * (only someone with `teamMembers` — i.e. direct reports at all — has
     * more than one person to choose between; `assigneeOptions` for
     * anyone else is just "Myself"). Reuses `teamMembers.length` rather
     * than a separate permission helper since that's the exact same
     * signal the existing single "Assign To" field's own options list
     * already keys off of.
     */
    canAssignToMultiple() {
      return !this.isEditMode && this.assigneeOptions.length > 1 && this.detailsEditable
    },
    /** `null` until a submit attempt actually fails on this — see `multiAssigneeSubmitAttempted`'s doc — mirrors `weeklyDaysError` just below in spirit. */
    multiAssigneeError() {
      if (!this.multiAssigneeSubmitAttempted) return null
      if (!this.assignToMultiple || this.form.assignedToList.length > 0) return null
      return 'Select at least one person to assign this task to.'
    },
    assigneeOptions() {
      const empId = getEmpId(this.currentUser)
      const options = []
      const seen = new Set()
      const addOption = (id, label) => {
        const sid = String(id)
        if (!sid || seen.has(sid)) return
        seen.add(sid)
        options.push({ value: sid, label })
      }

      if (this.resolvedProject) {
        const members = normalizeMembers(reconcileProjectMembers(this.resolvedProject).members)
        addOption(empId, 'Myself')
        members.forEach(member => {
          const id = String(member.empId)
          if (id === empId) return
          addOption(id, member.name || id)
        })
      } else {
        addOption(empId, 'Myself')
        this.teamMembers.forEach(member => {
          const id = String(member.emp_id ?? member.id)
          if (id === empId) return
          const name = member.first_name
            ? `${member.first_name} ${member.last_name || ''}`.trim()
            : (member.name || member.title || id)
          addOption(id, name)
        })
      }

      if (this.isEditMode && this.form.assignedTo) {
        const currentId = String(this.form.assignedTo)
        if (!seen.has(currentId)) {
          const name = this.task?.assignedToName ?? this.task?.assigned_to_name ?? currentId
          addOption(currentId, name)
        }
      }

      return options
    },
    /** Emp ids allowed in the assignee picker for the current project context. */
    validAssigneeIds() {
      return this.assigneeOptions.map(option => String(option.value))
    },
    /** Context hint when a project narrows the assignee list to its team. */
    assigneeScopeHint() {
      if (!this.resolvedProject) return null
      const others = this.assigneeOptions.length - 1
      if (others <= 0) return 'Only you are on this project team.'
      const label = others === 1 ? '1 teammate' : `${others} teammates`
      return `Showing ${label} from ${this.resolvedProject.name || 'this project'}.`
    },
    /** Button-content label for the multi-assignee dropdown — a plain `<b-dropdown>` toggle has no built-in "N selected" summary the way `v-select`'s tag-chip UI gave one for free, so this reproduces it: up to 2 names spelled out, then "+N more" beyond that. */
    assignedToListSummary() {
      if (!this.form.assignedToList.length) return 'Select people…'
      const names = this.form.assignedToList.map(id => {
        const match = this.assigneeOptions.find(option => String(option.value) === String(id))
        return match ? match.label : id
      })
      if (names.length <= 2) return names.join(', ')
      return `${names.slice(0, 2).join(', ')} +${names.length - 2} more`
    },
    /** Getter/setter proxy so the "No end date" checkbox and the `flat-pickr` field share one source of truth without a separate watcher — checking it simply clears the date; unchecking it just reveals the (still-empty, until picked) date field. */
    noEndDate: {
      get() {
        return !this.form.recurrenceEndDate
      },
      set(checked) {
        if (checked) this.form.recurrenceEndDate = null
      },
    },
    intervalUnitLabel() {
      const unit = { daily: 'day(s)', weekly: 'week(s)', monthly: 'month(s)' }[this.form.recurrenceFrequency]
      return unit || ''
    },
    /** Weekly + specific days chosen is a deliberately separate mode from weekly + interval (see `computeNextRecurrenceDate()`'s doc in `task-config.js`) — the interval input is disabled rather than hidden, so switching back to "no specific days" doesn't lose whatever interval the user had already set. */
    recurrenceIntervalDisabled() {
      return this.form.recurrenceFrequency === 'weekly' && this.form.recurrenceDaysOfWeek.length > 0
    },
    /** `null` until a submit attempt actually fails on this — see `weeklyDaysSubmitAttempted`'s doc — and auto-clears the moment the underlying condition resolves (a day gets picked), with no separate reset needed anywhere. */
    weeklyDaysError() {
      if (!this.weeklyDaysSubmitAttempted) return null
      if (!this.form.isRecurring || this.form.recurrenceFrequency !== 'weekly') return null
      if (this.form.recurrenceDaysOfWeek.length > 0) return null
      return 'Select at least one day.'
    },
    /** Newest-first — regardless of whether `historyEntries` came back already-sorted from a dedicated backend endpoint or as an append-ordered `task.changeHistory` fallback array. */
    sortedHistoryEntries() {
      return [...this.historyEntries].sort((a, b) => new Date(b.changedAt) - new Date(a.changedAt))
    },
    resolvedProject() {
      const pid = this.isEditMode
        ? (this.task?.projectId ?? this.task?.project_id ?? this.form.projectId)
        : this.form.projectId
      if (pid == null) return null
      const list = this.allProjects.length ? this.allProjects : this.projects
      return list.find(p => String(getProjectId(p) ?? p.id) === String(pid)) || null
    },
    assigneeValidationMessage() {
      if (this.resolvedProject) {
        return 'You can only assign this task to members of the selected project.'
      }
      return 'You can only assign tasks to yourself or your direct reports.'
    },
    effectiveStatusOptions() {
      if (!this.isEditMode || !this.resolvedProject || !this.task) return this.statusOptions
      if (this.isResponsibilityMode) return this.statusOptions
      return filterAllowedStatusOptions(
        this.currentUser,
        this.resolvedProject,
        this.task,
        this.statusOptions,
      )
    },
  },
  watch: {
    value(isOpen) {
      if (isOpen) this.resetForm()
    },
    /** Fires the moment the user actively SWITCHES to Monthly — see `applyDefaultDayOfMonth()`'s doc for why this is also called a second time, from `resetForm()`, to cover opening an already-Monthly task for edit. */
    'form.recurrenceFrequency'() {
      this.applyDefaultDayOfMonth()
    },
    'form.projectId'() {
      this.syncAssigneeToProjectScope()
      this.syncProjectStatusFromResolved()
    },
    attachmentFile(file) {
      if (file) {
        this.removeAttachment = false
        this.replacingAttachment = false
      }
    },
  },
  methods: {
    resetForm() {
      if (this.task) {
        this.form = {
          id: taskId(this.task),
          title: this.task.title || '',
          description: this.task.description || '',
          status: this.task.status || DEFAULT_TASK_STATUS,
          priority: this.task.priority || DEFAULT_TASK_PRIORITY,
          assignedTo: this.task.assignedTo != null ? String(this.task.assignedTo) : getEmpId(this.currentUser),
          dueDate: this.task.dueDate || null,
          dueTime: this.task.dueTime || null,
          isRecurring: !!this.task.isRecurring,
          recurrenceFrequency: this.task.recurrenceFrequency || DEFAULT_RECURRENCE_FREQUENCY,
          recurrenceInterval: this.task.recurrenceInterval || 1,
          recurrenceDaysOfWeek: Array.isArray(this.task.recurrenceDaysOfWeek) ? [...this.task.recurrenceDaysOfWeek] : [],
          recurrenceDayOfMonth: this.task.recurrenceDayOfMonth != null ? Number(this.task.recurrenceDayOfMonth) : null,
          recurrenceEndDate: this.task.recurrenceEndDate || null,
          recurrenceLastGeneratedDate: this.task.recurrenceLastGeneratedDate || null,
          recurringTaskId: this.task.recurringTaskId || null,
          projectId: normalizeProjectId(this.task.projectId ?? this.task.project_id),
          projectName: this.task.projectName ?? this.task.project_name ?? null,
        }
        if (!this.form.projectId) this.form.projectName = null
      } else {
        this.form = blankForm(this.currentUser, this.presetAssignee)
        const presetId = normalizeProjectId(this.presetProjectId)
        if (presetId != null) {
          const proj = (this.projects || []).find(p => String(p.id) === String(presetId))
          if (proj) {
            this.form.projectId = String(presetId)
            this.form.projectName = proj.name
          }
        }
      }
      this.syncProjectStatusFromResolved()
      this.weeklyDaysSubmitAttempted = false
      this.assignToMultiple = false
      this.multiAssigneeSubmitAttempted = false
      this.historyPanelOpen = false
      this.historyEntries = []
      this.attachmentFile = null
      this.removeAttachment = false
      this.replacingAttachment = false
      this.existingAttachment = this.task ? normalizeTaskAttachment(this.task) : null
      // Covers opening an EXISTING Monthly recurring task (created before
      // this field existed, so `recurrenceDayOfMonth` is `null`) for edit —
      // the `form.recurrenceFrequency` watcher above only fires on an
      // ACTIVE switch, which a plain "open this already-Monthly task"
      // never triggers.
      this.applyDefaultDayOfMonth()
      this.loadHistory()
      this.$nextTick(() => {
        if (this.$refs.taskFormRules) this.$refs.taskFormRules.reset()
      })
    },
    syncProjectStatusFromResolved() {
      this.projectStatus = this.resolvedProject
        ? normalizeProjectStatus(this.resolvedProject)
        : PROJECT_STATUSES.ACTIVE
    },
    /** Non-null when the PM changed project status relative to the loaded project. */
    buildProjectStatusUpdate() {
      if (!this.canEditProjectStatus || !this.resolvedProject) return null
      const next = normalizeProjectStatus({ status: this.projectStatus })
      const current = normalizeProjectStatus(this.resolvedProject)
      if (next === current) return null
      return {
        projectId: getProjectId(this.resolvedProject),
        status: next,
      }
    },
    withProjectStatusUpdate(payload) {
      const projectStatusUpdate = this.buildProjectStatusUpdate()
      return projectStatusUpdate ? { ...payload, projectStatusUpdate } : payload
    },
    syncAssigneeToProjectScope() {
      const validIds = this.validAssigneeIds
      if (this.form.assignedTo != null && !validIds.includes(String(this.form.assignedTo))) {
        this.form.assignedTo = this.currentEmpId
      }
      if (this.assignToMultiple && this.form.assignedToList.length) {
        this.form.assignedToList = this.form.assignedToList.filter(id => validIds.includes(String(id)))
      }
    },
    clearAttachment() {
      this.attachmentFile = null
      this.replacingAttachment = false
      if (this.existingAttachment) {
        this.removeAttachment = true
      }
    },
    replaceAttachment() {
      this.replacingAttachment = true
      this.removeAttachment = false
      this.$nextTick(() => {
        const input = document.getElementById('task-attachment-replace')
        if (input) input.click()
      })
    },
    /** Attachment fields to merge onto the submit payload (create/edit/multi). */
    attachmentSubmitFields() {
      const fields = {
        attachmentFile: this.attachmentFile || null,
        removeAttachment: !!this.removeAttachment && !this.attachmentFile,
      }
      if (this.attachmentFile) {
        fields.attachmentName = this.attachmentFile.name || 'Attachment'
        fields.attachmentUrl = null
        fields.file_path = null
      } else if (fields.removeAttachment) {
        fields.attachmentName = null
        fields.attachmentUrl = null
        fields.file_path = null
      } else if (this.existingAttachment) {
        fields.attachmentName = this.existingAttachment.name
        fields.attachmentUrl = this.existingAttachment.url
        fields.file_path = this.existingAttachment.path
      }
      return fields
    },
    /**
     * Pre-fills `form.recurrenceDayOfMonth` from the current Due Date's
     * day-of-month — a nice-to-have UX default, never a forced/overridden
     * value: a no-op whenever the field is frequency-irrelevant (not
     * Monthly), already has an explicit value (never clobbers a real
     * choice — including one just loaded from an existing task in
     * `resetForm()`), or there's no Due Date yet to derive a default from.
     * Called from two places — see each call site's own doc — but is
     * idempotent/side-effect-free to call redundantly.
     */
    applyDefaultDayOfMonth() {
      if (this.form.recurrenceFrequency !== 'monthly' || this.form.recurrenceDayOfMonth != null || !this.form.dueDate) return
      const parsed = new Date(this.form.dueDate)
      if (!Number.isNaN(parsed.getTime())) this.form.recurrenceDayOfMonth = parsed.getDate()
    },
    /**
     * Tries the dedicated `GET /pm/tasks/:id/history` endpoint first; falls
     * back to whatever `task.changeHistory` already carries locally (no
     * extra fetch needed — `task` is already the full, already-loaded
     * object `TaskBoard.vue` passed in as a prop) when that endpoint isn't
     * implemented yet, or on any other error. Never throws, never blocks
     * the rest of the modal — a history-load failure only affects the
     * (collapsed-by-default) History panel.
     */
    async loadHistory() {
      if (!this.isEditMode || this.isResponsibilityMode) return
      this.historyLoading = true
      try {
        const id = taskId(this.task)
        const res = await taskService.getTaskHistory(id)
        if (res && res.status === 'OK' && Array.isArray(res.data)) {
          this.historyEntries = res.data
        } else {
          this.historyEntries = normalizeChangeHistory(
            this.task && (this.task.changeHistory ?? this.task.change_history),
          )
        }
      } catch (err) {
        console.warn('[task] history load error (non-blocking):', err)
        this.historyEntries = normalizeChangeHistory(
          this.task && (this.task.changeHistory ?? this.task.change_history),
        )
      } finally {
        this.historyLoading = false
      }
    },
    /** Fallback display for an entry that (e.g. from a future backend shape) didn't already carry a pre-formatted `oldDisplay`/`newDisplay`. */
    formatHistoryValue(value) {
      if (value === undefined || value === null || value === '') return '—'
      if (Array.isArray(value)) return value.length ? value.join(', ') : '—'
      return String(value)
    },
    formatHistoryDate(value) {
      if (!value) return '-'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return '-'
      return date.toLocaleString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
      })
    },
    async handleOk(bvModalEvt) {
      bvModalEvt.preventDefault()
      const projectStatusUpdate = this.buildProjectStatusUpdate()

      if (this.isResponsibilityMode) {
        if (!this.statusEditable && !projectStatusUpdate) {
          this.$emit('submit-error', 'Project responsibilities are managed from the Projects team panel.')
          return
        }
        if (this.statusEditable) {
          const valid = await this.$refs.taskFormRules.validate()
          if (!valid) {
            this.$emit('submit-error', 'Please fill in all required fields before saving.')
            return
          }
        }
        const statusChanged = this.statusEditable
          && normalizeTaskStatus({ status: this.form.status }) !== normalizeTaskStatus(this.task)
        if (!statusChanged && !projectStatusUpdate) {
          this.$emit('input', false)
          return
        }
        this.$emit('submit', {
          mode: 'responsibility',
          id: this.form.id,
          status: this.form.status,
          statusChanged,
          projectStatusUpdate,
        })
        return
      }

      if (this.isReadOnlyMode) {
        this.$emit('submit-error', 'Only the task creator can update this task.')
        return
      }

      // PM can update project status even when task fields are locked.
      if (!this.detailsEditable && !this.statusEditable && !this.priorityEditable) {
        if (projectStatusUpdate) {
          this.$emit('submit', { mode: 'project-status-only', ...projectStatusUpdate })
          return
        }
        this.$emit('submit-error', 'Only the task creator can update this task.')
        return
      }

      const valid = await this.$refs.taskFormRules.validate()
      if (!valid) {
        this.$emit('submit-error', 'Please fill in all required fields before saving.')
        return
      }

      // Vee-validate's `required` rule doesn't naturally cover "at least one
      // checkbox in this group" for `recurrenceDaysOfWeek` — checked
      // manually here, same "guard right before emit" pattern already used
      // below for `assignableEmpIds`.
      if (this.form.isRecurring && this.form.recurrenceFrequency === 'weekly' && !this.form.recurrenceDaysOfWeek.length) {
        this.weeklyDaysSubmitAttempted = true
        this.$emit('submit-error', 'Pick at least one day of the week for this recurring task.')
        return
      }

      if (this.isEditMode && !this.detailsEditable && this.priorityEditable) {
        // Priority-only override — submit ONLY the priority change so we
        // never accidentally clobber fields this user isn't allowed to edit.
        this.$emit('submit', this.withProjectStatusUpdate({
          mode: 'priority-only',
          id: this.form.id,
          priority: this.form.priority,
        }))
        return
      }

      if (this.isStatusOnlyMode) {
        const projectDenial = getProjectStatusDenialReason(
          this.currentUser,
          this.resolvedProject,
          this.task,
          this.form.status,
        )
        if (projectDenial) {
          this.$emit('submit-error', projectDenial)
          return
        }
        // Assignee whose task was created by someone else — submit ONLY the
        // status change, mirroring the priority-only path above, so a
        // locked field's (unchanged) value can never be sent back as an edit.
        this.$emit('submit', this.withProjectStatusUpdate({
          mode: 'status-only',
          id: this.form.id,
          status: this.form.status,
        }))
        return
      }

      if (!this.isEditMode && this.assignToMultiple) {
        // "Assign to multiple?" — mirrors the `weeklyDaysError` "at least
        // one" pattern above, then reuses the exact same `assignableEmpIds`
        // guard as the single-assignee path below, just checked against
        // every selection instead of one. `TaskBoard.vue#handleSubmit`
        // does the actual clone-into-N-tasks work for `mode: 'create-multi'`
        // — this modal's only job is collecting + validating the list.
        if (!this.form.assignedToList.length) {
          this.multiAssigneeSubmitAttempted = true
          this.$emit('submit-error', 'Select at least one person to assign this task to.')
          return
        }
        const assignable = this.validAssigneeIds
        const hasInvalidAssignee = this.form.assignedToList.some(empId => !assignable.includes(String(empId)))
        if (hasInvalidAssignee) {
          this.$emit('submit-error', this.assigneeValidationMessage)
          return
        }
        const projectId = normalizeProjectId(this.form.projectId)
        this.$emit('submit', {
          mode: 'create-multi',
          ...this.form,
          projectId,
          projectName: projectId ? this.resolveProjectName(projectId) : null,
          assignedToList: this.form.assignedToList.map(String),
          ...this.attachmentSubmitFields(),
        })
        return
      }

      if (this.form.assignedTo != null && !this.validAssigneeIds.includes(String(this.form.assignedTo))) {
        this.$emit('submit-error', this.assigneeValidationMessage)
        return
      }

      if (this.isEditMode && this.task && normalizeTaskStatus({ status: this.form.status }) !== normalizeTaskStatus(this.task)) {
        const projectDenial = getProjectStatusDenialReason(
          this.currentUser,
          this.resolvedProject,
          this.task,
          this.form.status,
        )
        if (projectDenial) {
          this.$emit('submit-error', projectDenial)
          return
        }
      }

      const projectId = normalizeProjectId(this.form.projectId)
      const projectName = projectId ? this.resolveProjectName(projectId) : null

      this.$emit('submit', this.withProjectStatusUpdate({
        mode: this.isEditMode ? 'edit' : 'create',
        ...this.form,
        projectId,
        projectName,
        ...this.attachmentSubmitFields(),
      }))
    },
    resolveProjectName(projectId) {
      if (normalizeProjectId(projectId) == null) return null
      const proj = (this.projects || []).find(p => String(p.id) === String(projectId))
      return proj ? proj.name : this.form.projectName
    },
    onHidden() {
      this.$emit('input', false)
    },
  },
}
</script>

<style lang="scss" scoped>
.task-form-section-title {
  color: #5e5873;
}

.task-form-section {
  margin-bottom: 0.25rem;
}

.min-width-0 {
  min-width: 0;
}

// The multi-assignee dropdown's toggle button is given the `.form-control`
// class (see `toggle-class` in the template) so it inherits the exact same
// height/padding/border/font as every other field in this modal —
// `variant="link"` (rather than e.g. `outline-secondary`) is deliberate:
// `.btn-link` itself has no background/border of its own to fight
// `.form-control`'s, so there's nothing left to override except the
// link-specific hover/focus text-decoration.
.assignee-multiselect-toggle {
  width: 100%;
  font-weight: 400;
  text-align: left;
  text-decoration: none !important;

  &:hover,
  &:focus,
  &:active {
    text-decoration: none !important;
  }
}

.assignee-multiselect-menu {
  max-height: 220px;
  overflow-y: auto;
  padding: 0.5rem 0;
  min-width: 220px;

  .assignee-multiselect-item.custom-control {
    padding: 0.25rem 0.5rem 0.25rem 2.1rem;
    margin-bottom: 0;
    min-height: auto;
    width: 100%;
  }

  .assignee-multiselect-item .custom-control-label {
    cursor: pointer;
    line-height: 1.35;
  }
}

.task-history-toggle {
  font-weight: 500;
}

.task-history-list {
  max-height: 220px;
  overflow-y: auto;
}

.task-history-item {
  padding: 0.35rem 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  line-height: 1.5;

  &:last-child {
    border-bottom: none;
  }
}

.task-attachment-row {
  padding: 0.5rem 0.65rem;
  background: rgba(115, 103, 240, 0.06);
  border-radius: 0.357rem;
}

.task-attachment-card {
  background: rgba(0, 0, 0, 0.015);
}

.task-attachment-file ::v-deep .custom-file-label {
  border-style: dashed;
}
</style>
